// Every storefront string, in English and Lithuanian. Shop content itself (products, pages,
// store copy) comes translated from the API; this file only covers the interface.

/** Lithuanian has three count forms: 1 daiktas, 2 daiktai, 10 daiktų (21 daiktas, 22 daiktai…). */
export function ltPlural(n: number, one: string, few: string, many: string) {
  const n10 = n % 10;
  const n100 = n % 100;
  if (n10 === 1 && n100 !== 11) return one;
  if (n10 >= 2 && n10 <= 9 && (n100 < 12 || n100 > 19)) return few;
  return many;
}

export const en = {
  skipToContent: 'Skip to content',
  est: 'Old everyday things',
  common: {
    home: 'Home',
    shopAll: 'Everything',
    reduced: 'Reduced',
    tryAgain: 'Try again',
    somethingWrong: 'Something went wrong',
    networkError: 'We couldn’t reach the shop. Check your connection and try again.',
    loading: 'Loading…',
    close: 'Close',
    optional: 'Optional',
    free: 'Free',
    from: 'From',
    was: (price: string) => `Was ${price}`,
    items: (n: number) => `${n} ${n === 1 ? 'item' : 'items'}`,
    breadcrumb: 'Breadcrumb',
    continueShopping: 'Continue shopping',
  },
  offline: {
    title: 'We’ll be right back.',
    body: 'The shop is temporarily unavailable. Please try again in a moment.',
  },
  header: {
    shop: 'Shop',
    search: 'Search',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    categories: 'Rooms',
    collections: 'Collections',
    main: 'Main',
    siteNav: 'Site navigation',
    help: 'Help',
    language: 'Language',
  },
  footer: {
    shop: 'Shop',
    collections: 'Collections',
    help: 'Help',
    footer: 'Footer',
  },
  card: {
    sold: 'Sold',
    oneOfAKind: 'One of a kind',
    choose: 'Choose options',
    view: 'Have a look',
    colours: (n: number) => `${n} colours`,
  },
  shop: {
    search: 'Search',
    resultsFor: (q: string) => `Results for “${q}”`,
    filter: 'Filter',
    sortBy: 'Sort by',
    sortLabel: 'Sort items',
    sorts: {
      featured: 'Recommended',
      newest: 'Just in',
      'price-asc': 'Price: low to high',
      'price-desc': 'Price: high to low',
      name: 'Name: A–Z',
    },
    under: (p: string) => `Under ${p}`,
    over: (p: string) => `Over ${p}`,
    any: 'any',
    available: 'Available now',
    clearAll: 'Clear all',
    nothing: 'Nothing on this shelf — yet.',
    tryDifferent: 'Try a different word, or browse everything we have.',
    tryRemoving: 'Try removing a filter or two.',
    browseEverything: 'Browse everything',
    showing: (a: number, b: number) => `Showing ${a} of ${b}`,
    loadMore: 'Load more',
    closeFilters: 'Close filters',
    narrow: 'Narrow down items',
    show: (n: number) => `Show ${n} ${n === 1 ? 'item' : 'items'}`,
    category: 'Room',
    decade: 'Decade',
    condition: 'Condition',
    availability: 'Availability',
    price: 'Price',
    hideSold: 'Hide sold items',
    minPrice: 'Minimum price',
    maxPrice: 'Maximum price',
    min: 'Min',
    max: 'Max',
  },
  product: {
    notAvailable: 'This item isn’t available',
    vat: 'Price includes VAT',
    select: 'Select options',
    sold: 'Sold — have a look at what else is on the shelves',
    onlyOne: 'The only one we have — once it’s gone, it’s gone',
    onlyLeft: (n: number) => `Only ${n} left`,
    inStock: 'In stock, ready to post',
    buyOnVinted: (p: string) => `Buy on Vinted — ${p}`,
    buyShort: (p: string) => `Vinted — ${p}`,
    notOnVinted: 'Coming to Vinted soon',
    soldButton: 'Sold',
    checked: 'Cleaned, checked and tested by hand',
    about: 'About this item',
    decade: 'Decade',
    condition: 'Condition',
    origin: 'Made in',
    material: 'Material',
    dimensions: 'Size',
    notes: 'Condition notes',
    details: 'More about it',
    care: 'Care',
    careBody: 'Old things like gentle handling: wash glass, porcelain and enamel by hand, and keep cast iron dry.',
    careMore: 'Read our',
    careLink: 'condition & care guide',
    buying: 'Buying & delivery',
    buyingBody: 'Every item is sold through Vinted: the button opens its listing, where you pay and choose how it’s delivered. Questions? Message us there.',
    more: 'More',
    relatedEyebrow: 'More from the shelves',
    related: 'You may also like',
    showImage: (i: number) => `Show image ${i}`,
    image: (i: number) => `Image ${i}`,
  },
  notFound: {
    seo: 'Not found',
    title: 'We couldn’t find that page',
    body: 'It may have moved, been sold, or never existed. Let’s find you something else.',
  },
  search: {
    title: 'Search the shop',
    description: 'Type to search items and rooms',
    placeholder: 'Search mugs, jars, clocks…',
    label: 'Search',
    close: 'Close search',
    popular: 'Popular',
    none: (term: string) => `Nothing for “${term}”. Try another word, like “mug” or “clock”.`,
    items: 'Items',
    viewAll: (n: number) => `View all ${n} results`,
  },
};

export type Dictionary = typeof en;

export const lt: Dictionary = {
  skipToContent: 'Pereiti prie turinio',
  est: 'Seni kasdieniai daiktai',
  common: {
    home: 'Pradžia',
    shopAll: 'Visi daiktai',
    reduced: 'Nukainota',
    tryAgain: 'Bandyti dar kartą',
    somethingWrong: 'Kažkas nutiko',
    networkError: 'Nepavyko prisijungti prie parduotuvės. Patikrinkite ryšį ir bandykite dar kartą.',
    loading: 'Įkeliama…',
    close: 'Uždaryti',
    optional: 'Neprivaloma',
    free: 'Nemokamai',
    from: 'Nuo',
    was: (price: string) => `Buvo ${price}`,
    items: (n: number) => `${n} ${ltPlural(n, 'daiktas', 'daiktai', 'daiktų')}`,
    breadcrumb: 'Naršymo kelias',
    continueShopping: 'Tęsti apsipirkimą',
  },
  offline: {
    title: 'Netrukus grįšime.',
    body: 'Parduotuvė laikinai nepasiekiama. Pabandykite po akimirkos.',
  },
  header: {
    shop: 'Parduotuvė',
    search: 'Paieška',
    openMenu: 'Atidaryti meniu',
    closeMenu: 'Uždaryti meniu',
    categories: 'Kambariai',
    collections: 'Rinkiniai',
    main: 'Pagrindinis meniu',
    siteNav: 'Svetainės meniu',
    help: 'Pagalba',
    language: 'Kalba',
  },
  footer: {
    shop: 'Parduotuvė',
    collections: 'Rinkiniai',
    help: 'Pagalba',
    footer: 'Poraštė',
  },
  card: {
    sold: 'Parduota',
    oneOfAKind: 'Vienintelis',
    choose: 'Pasirinkti',
    view: 'Apžiūrėti',
    colours: (n: number) => `${n} ${ltPlural(n, 'spalva', 'spalvos', 'spalvų')}`,
  },
  shop: {
    search: 'Paieška',
    resultsFor: (q: string) => `Paieška: „${q}“`,
    filter: 'Filtrai',
    sortBy: 'Rikiuoti',
    sortLabel: 'Rikiuoti daiktus',
    sorts: {
      featured: 'Rekomenduojami',
      newest: 'Naujausi',
      'price-asc': 'Kaina: nuo mažiausios',
      'price-desc': 'Kaina: nuo didžiausios',
      name: 'Pavadinimas: A–Ž',
    },
    under: (p: string) => `Iki ${p}`,
    over: (p: string) => `Virš ${p}`,
    any: 'bet kokia',
    available: 'Galima įsigyti',
    clearAll: 'Išvalyti viską',
    nothing: 'Šioje lentynoje kol kas tuščia.',
    tryDifferent: 'Pabandykite kitą žodį arba peržiūrėkite visus daiktus.',
    tryRemoving: 'Pabandykite pašalinti vieną ar kelis filtrus.',
    browseEverything: 'Žiūrėti visus daiktus',
    showing: (a: number, b: number) => `Rodoma ${a} iš ${b}`,
    loadMore: 'Rodyti daugiau',
    closeFilters: 'Uždaryti filtrus',
    narrow: 'Susiaurinti paiešką',
    show: (n: number) => `Rodyti ${n} ${ltPlural(n, 'daiktą', 'daiktus', 'daiktų')}`,
    category: 'Kambarys',
    decade: 'Dešimtmetis',
    condition: 'Būklė',
    availability: 'Prieinamumas',
    price: 'Kaina',
    hideSold: 'Slėpti parduotus',
    minPrice: 'Mažiausia kaina',
    maxPrice: 'Didžiausia kaina',
    min: 'Nuo',
    max: 'Iki',
  },
  product: {
    notAvailable: 'Šio daikto nebėra',
    vat: 'Kaina su PVM',
    select: 'Pasirinkite',
    sold: 'Parduota — apžiūrėkite, ką dar turime lentynose',
    onlyOne: 'Turime tik vieną — kai parduosime, nebebus',
    onlyLeft: (n: number) => `Liko tik ${n}`,
    inStock: 'Yra, paruošta siųsti',
    buyOnVinted: (p: string) => `Pirkti Vinted — ${p}`,
    buyShort: (p: string) => `Vinted — ${p}`,
    notOnVinted: 'Netrukus bus Vinted',
    soldButton: 'Parduota',
    checked: 'Išvalyta, patikrinta ir išbandyta rankomis',
    about: 'Apie šį daiktą',
    decade: 'Laikotarpis',
    condition: 'Būklė',
    origin: 'Pagaminta',
    material: 'Medžiaga',
    dimensions: 'Matmenys',
    notes: 'Būklės pastabos',
    details: 'Daugiau apie jį',
    care: 'Priežiūra',
    careBody: 'Seni daiktai mėgsta švelnumą: stiklą, porcelianą ir emalį plaukite rankomis, o ketų laikykite sausą.',
    careMore: 'Skaitykite mūsų',
    careLink: 'būklės ir priežiūros gidą',
    buying: 'Pirkimas ir pristatymas',
    buyingBody: 'Visi daiktai parduodami per Vinted: mygtukas atidaro skelbimą, kuriame sumokate ir pasirenkate pristatymą. Klausimų? Parašykite mums ten.',
    more: 'Daugiau',
    relatedEyebrow: 'Daugiau iš lentynų',
    related: 'Jums gali patikti',
    showImage: (i: number) => `Rodyti ${i} nuotrauką`,
    image: (i: number) => `${i} nuotrauka`,
  },
  notFound: {
    seo: 'Nerasta',
    title: 'Tokio puslapio neradome',
    body: 'Galbūt jis perkeltas, daiktas jau parduotas arba tokio puslapio niekada nebuvo. Raskime jums ką nors kita.',
  },
  search: {
    title: 'Ieškoti parduotuvėje',
    description: 'Įveskite, ko ieškote',
    placeholder: 'Ieškokite puodelių, stiklainių, laikrodžių…',
    label: 'Paieška',
    close: 'Uždaryti paiešką',
    popular: 'Populiaru',
    none: (term: string) => `Pagal „${term}“ nieko neradome. Pabandykite kitą žodį, pvz., „puodelis“ arba „laikrodis“.`,
    items: 'Daiktai',
    viewAll: (n: number) => `Žiūrėti visus rezultatus (${n})`,
  },
};
